import { useState } from 'react';
import { DateTime } from 'luxon';
import IconAccepted from '../../../components/icons/IconAccepted';
import IconWrong from '../../../components/icons/IconWrong';
import IconArrow from '../../../components/icons/IconArrow';

// Reconstructs "exactly what the student saw and answered" in an
// embed_conversation, from the real recorded StudentConversationStep rows.
// Modernized to match the student platform's own question-card look (every
// option visible, radio-styled, reusing the exact same
// .course-preview-option-row markup/CSS AnsweredMultipleChoice.jsx already
// uses for the other exercise types) rather than showing only the one
// response the student picked - a teacher reviewing this needs to see what
// the OTHER choices were too, not just take the highlighted one on faith.
//
// `answerConnector.fromId.answerConnectorsFrom` (every sibling connector
// at that question, `isRight` intact) is confirmed present on this
// endpoint's real response - Jackson serializes it in full since Answer
// only strips `fromId`/`nextId` off each nested connector, not the list
// itself. Free-text nodes (`step.freeTextAnswer` set) have no sibling
// options at all - shown as a plain written-answer box instead, same
// convention as the fill_blank/essay read-only renderers.
export default function ConversationStepHistory({ progress, steps }) {
   const [index, setIndex] = useState(0);

   if (!steps || steps.length === 0) {
      return <p className='workspace-activity-empty-hint'>Студент ще не почав цю розмову.</p>;
   }

   const correctCount = steps.filter(s => s.isCorrect === true).length;
   const wrongCount = steps.filter(s => s.isCorrect === false).length;

   const clampedIndex = Math.min(index, steps.length - 1);
   const step = steps[clampedIndex];
   const connector = step.answerConnector;
   const question = connector && connector.fromId ? connector.fromId.text : '';
   const isTextAnswer = step.freeTextAnswer != null;
   const options = (!isTextAnswer && connector && connector.fromId && connector.fromId.answerConnectorsFrom)
      ? connector.fromId.answerConnectorsFrom.filter(c => !(c.clasification && c.clasification.id === 1))
      : [];

   const goPrev = () => setIndex(i => Math.max(0, i - 1));
   const goNext = () => setIndex(i => Math.min(steps.length - 1, i + 1));

   return (
      <div className='workspace-conversation-history'>
         <div className='workspace-conversation-summary'>
            <span className={'workspace-conversation-status' + (progress && progress.completed ? ' completed' : ' in-progress')}>
               {progress && progress.completed ? 'Завершено' : 'В процесі'}
            </span>
            <span className='workspace-conversation-count-tag correct'><IconAccepted style={{ width: 12, height: 12, fill: '#30e846' }} />{correctCount}</span>
            <span className='workspace-conversation-count-tag wrong'><IconWrong style={{ width: 12, height: 12, fill: '#ff5140' }} />{wrongCount}</span>
         </div>

         <div className='workspace-conversation-progress-bar'>
            <div className='workspace-conversation-progress-fill' style={{ width: ((clampedIndex + 1) / steps.length * 100) + '%' }} />
         </div>

         <div className='workspace-conversation-stepper'>
            <button type='button' className='workspace-conversation-nav-btn' onClick={goPrev} disabled={clampedIndex === 0} aria-label='Попереднє питання'>
               <IconArrow style={{ width: 12, height: 12, fill: 'currentColor', transform: 'rotate(180deg)' }} />
            </button>
            <div className='course-preview-question workspace-conversation-step'>
               <span className='workspace-conversation-step-counter'>Питання {clampedIndex + 1} з {steps.length}</span>
               {question ? <p className='course-preview-question-text'>{question}</p> : ''}

               {isTextAnswer ? (
                  <div className='workspace-answered-essay-text workspace-conversation-freetext'>{step.freeTextAnswer}</div>
               ) : options.length > 0 ? (
                  <div className='course-preview-options'>
                     {options.map(o => {
                        const isPicked = connector && o.id === connector.id;
                        const isCorrectOption = o.isRight && o.isRight.id === 1;
                        const cls = 'course-preview-option-row'
                           + (isPicked && isCorrectOption ? ' correct' : '')
                           + (isPicked && !isCorrectOption ? ' wrong' : '')
                           + (!isPicked && isCorrectOption ? ' correct-hint' : '');
                        return (
                           <label className={cls} key={o.id}>
                              <input type='radio' checked={isPicked} readOnly disabled />
                              <span>{o.text}</span>
                              {!isPicked && isCorrectOption ? <em className='workspace-answered-correct-tag'>правильна відповідь</em> : ''}
                           </label>
                        );
                     })}
                  </div>
               ) : (
                  <div className='workspace-answered-essay-text workspace-conversation-freetext'>{connector ? connector.text : ''}</div>
               )}

               {step.dAdd ? (
                  <span className='workspace-conversation-step-date'>
                     {DateTime.fromISO(step.dAdd).toFormat('dd.MM.yyyy HH:mm')}
                  </span>
               ) : ''}
            </div>
            <button type='button' className='workspace-conversation-nav-btn' onClick={goNext} disabled={clampedIndex === steps.length - 1} aria-label='Наступне питання'>
               <IconArrow style={{ width: 12, height: 12, fill: 'currentColor' }} />
            </button>
         </div>
      </div>
   );
}
